import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useAnimation } from 'framer-motion';

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
`;

const GlobalStyle = styled.div`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Orbitron', sans-serif;
    background: radial-gradient(ellipse at top, #1a1a2e 0%, #16213e 25%, #0a0a12 100%);
    color: #ffffff;
    min-height: 100vh;
    overflow-x: hidden;
    position: relative;
  }

  body::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(138, 43, 226, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(0, 212, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
    z-index: 1;
  }
`;

const ParticleContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  border-radius: 50%;
  animation: ${float} ${props => props.duration}s ease-in-out infinite,
             ${pulse} ${props => props.pulseDuration}s ease-in-out infinite;
  filter: blur(${props => props.blur}px);
  box-shadow: 0 0 ${props => props.size * 2}px ${props => props.color};
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 3;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  text-align: center;
  position: relative;
  backdrop-filter: blur(2px);
  
  @media (min-width: 768px) {
    padding: 4rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 80%;
    height: 80%;
    background: radial-gradient(circle, rgba(0, 212, 255, 0.03) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 900;
  background: linear-gradient(135deg, #00d4ff 0%, #8a2be2 50%, #00d4ff 100%);
  background-size: 200% 100%;
  background-position: 0% 50%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1.5rem;
  text-shadow: 0 0 30px rgba(0, 212, 255, 0.5);
  position: relative;
  z-index: 10;
  letter-spacing: 2px;
  
  @media (min-width: 768px) {
    font-size: 4.5rem;
    letter-spacing: 3px;
  }
  
  @media (min-width: 1024px) {
    font-size: 6rem;
    letter-spacing: 4px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #00d4ff 0%, #8a2be2 50%, #00d4ff 100%);
    background-size: 200% 100%;
    background-position: 0% 50%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: blur(3px);
    z-index: -1;
  }

  &::after {
    content: 'Superpositions.ai';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    filter: blur(8px);
    z-index: -2;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 2rem;
  max-width: 700px;
  opacity: 0.9;
  font-weight: 400;
  text-align: center;
  position: relative;
  z-index: 10;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (min-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 3rem;
    line-height: 1.9;
  }

  @media (min-width: 1024px) {
    font-size: 1.4rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -20px;
    right: -20px;
    bottom: -10px;
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.02) 0%, rgba(138, 43, 226, 0.02) 100%);
    border-radius: 20px;
    z-index: -1;
    backdrop-filter: blur(5px);
  }
`;

const PriceContainer = styled(motion.div)`
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const Price = styled.div`
  font-size: 3rem;
  font-weight: 900;
  color: #00d4ff;
  text-shadow: 
    0 0 20px rgba(0, 212, 255, 0.8),
    0 0 40px rgba(0, 212, 255, 0.6),
    0 0 60px rgba(0, 212, 255, 0.4);
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 10;
  letter-spacing: 2px;
  
  @media (min-width: 768px) {
    font-size: 4rem;
    letter-spacing: 3px;
  }

  @media (min-width: 1024px) {
    font-size: 5rem;
    letter-spacing: 4px;
  }

  &::before {
    content: '$30,000';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    color: rgba(0, 212, 255, 0.3);
    filter: blur(2px);
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(0, 212, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    z-index: -2;
  }
`;

const PriceNote = styled.div`
  font-size: 1rem;
  opacity: 0.7;
  
  @media (min-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CTAButton = styled(motion.button)`
  background: linear-gradient(135deg, #00d4ff 0%, #8a2be2 50%, #00d4ff 100%);
  background-size: 200% 100%;
  border: 2px solid rgba(0, 212, 255, 0.3);
  border-radius: 50px;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  color: white;
  cursor: pointer;
  font-family: 'Orbitron', sans-serif;
  text-transform: uppercase;
  letter-spacing: 2px;
  box-shadow: 
    0 4px 15px rgba(0, 212, 255, 0.4),
    0 0 20px rgba(0, 212, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  z-index: 10;
  
  &:hover {
    background-position: 100% 0;
    box-shadow: 
      0 6px 20px rgba(0, 212, 255, 0.6),
      0 0 30px rgba(0, 212, 255, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
    border-color: rgba(0, 212, 255, 0.6);
  }

  &:active {
    transform: translateY(-1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    border-radius: 50px;
    pointer-events: none;
  }
  
  @media (min-width: 768px) {
    padding: 1.2rem 2.5rem;
    font-size: 1.2rem;
    letter-spacing: 3px;
  }

  @media (min-width: 1024px) {
    padding: 1.4rem 3rem;
    font-size: 1.3rem;
  }
`;

const Sidebar = styled(motion.aside)`
  background: rgba(26, 26, 46, 0.4);
  backdrop-filter: blur(25px);
  border-left: 1px solid rgba(0, 212, 255, 0.3);
  padding: 2rem;
  position: relative;
  
  @media (max-width: 767px) {
    border-left: none;
    border-top: 1px solid rgba(0, 212, 255, 0.3);
  }
  
  @media (min-width: 768px) {
    width: 450px;
    min-height: 100vh;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 212, 255, 0.05) 0%,
      rgba(138, 43, 226, 0.05) 50%,
      rgba(0, 212, 255, 0.05) 100%
    );
    pointer-events: none;
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: 100%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(0, 212, 255, 0.8) 20%,
      rgba(138, 43, 226, 0.8) 50%,
      rgba(0, 212, 255, 0.8) 80%,
      transparent 100%
    );
    
    @media (max-width: 767px) {
      display: none;
    }
  }
`;

const SidebarTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: #00d4ff;
  text-align: center;
  
  @media (min-width: 768px) {
    text-align: left;
  }
`;

const DomainCard = styled(motion.a)`
  display: block;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 20px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  text-decoration: none;
  color: inherit;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(0, 212, 255, 0.6);
    transform: translateY(-5px) scale(1.02);
    box-shadow: 
      0 10px 30px rgba(0, 212, 255, 0.3),
      0 0 20px rgba(0, 212, 255, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(0, 212, 255, 0.1),
      transparent
    );
    transition: left 0.6s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 212, 255, 0.03) 0%,
      rgba(138, 43, 226, 0.03) 100%
    );
    border-radius: 20px;
    pointer-events: none;
  }
`;

const DomainName = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #00d4ff 0%, #8a2be2 50%, #00d4ff 100%);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  position: relative;
  z-index: 10;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }

  ${DomainCard}:hover & {
    background-position: 100% 0;
    text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
  }
`;

const DomainDescription = styled.p`
  font-size: 0.9rem;
  opacity: 0.8;
  line-height: 1.5;
  position: relative;
  z-index: 10;
  transition: all 0.3s ease;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 1rem;
  }

  ${DomainCard}:hover & {
    opacity: 1;
    color: rgba(255, 255, 255, 0.95);
  }
`;

const domains = [
  {
    name: "Freshy.ai",
    description: "Ideal for AI freshness tracking, supply chain tech, or apps focused on newness.",
    url: "https://freshy.ai"
  },
  {
    name: "Spendy.ai",
    description: "Perfect for AI-driven finance, budgeting, and spending assistants.",
    url: "https://spendy.ai"
  },
  {
    name: "Superpositions.ai",
    description: "Great for quantum computing startups, AI research platforms, and futuristic tech.",
    url: "https://superpositions.ai"
  },
  {
    name: "Syngen.ai",
    description: "Versatile for synthetic biology, AI-generated content, and next-gen intelligence.",
    url: "https://syngen.ai"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.2,
      staggerChildren: 0.15
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0, scale: 0.9 },
  visible: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const cardVariants = {
  hidden: { x: 50, opacity: 0, scale: 0.9 },
  visible: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

// Particle Animation Component
const ParticleAnimation = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 15; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 6 + 2,
          color: Math.random() > 0.5 ? 'rgba(0, 212, 255, 0.4)' : 'rgba(138, 43, 226, 0.4)',
          duration: Math.random() * 10 + 10,
          pulseDuration: Math.random() * 3 + 2,
          blur: Math.random() * 2 + 1
        });
      }
      setParticles(newParticles);
    };

    generateParticles();
  }, []);

  return (
    <ParticleContainer>
      {particles.map(particle => (
        <Particle
          key={particle.id}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
          }}
          size={particle.size}
          color={particle.color}
          duration={particle.duration}
          pulseDuration={particle.pulseDuration}
          blur={particle.blur}
        />
      ))}
    </ParticleContainer>
  );
};

function App() {
  const handleBuyDomain = () => {
    const subject = encodeURIComponent("Inquiry about purchasing Superpositions.ai - $30,000");
    const body = encodeURIComponent("Hi, I'm interested in purchasing the domain Superpositions.ai for $30,000. Please provide more details about the purchase process.");
    window.location.href = `mailto:contact@yourdomain.com?subject=${subject}&body=${body}`;
  };

  return (
    <GlobalStyle>
      <ParticleAnimation />
      <Container>
        <MainContent>
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Title variants={itemVariants}>
              Superpositions.ai
            </Title>
            
            <Description variants={itemVariants}>
              This premium domain is for sale! Great for quantum computing startups, AI research platforms, and futuristic tech.
            </Description>
            
            <PriceContainer variants={itemVariants}>
              <Price>$30,000</Price>
              <PriceNote>Price negotiable</PriceNote>
            </PriceContainer>
            
            <CTAButton
              variants={itemVariants}
              onClick={handleBuyDomain}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Buy This Domain
            </CTAButton>
          </motion.div>
        </MainContent>
        
        <Sidebar
          initial={{ x: 60, opacity: 0, scale: 0.95 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, type: "spring", stiffness: 100, damping: 15 }}
        >
          <SidebarTitle>Premium AI Domains</SidebarTitle>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {domains.map((domain, index) => (
              <DomainCard
                key={domain.name}
                href={domain.url}
                target="_blank"
                rel="noopener noreferrer"
                variants={cardVariants}
                custom={index}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <DomainName>{domain.name}</DomainName>
                <DomainDescription>{domain.description}</DomainDescription>
              </DomainCard>
            ))}
          </motion.div>
        </Sidebar>
      </Container>
    </GlobalStyle>
  );
}

export default App; 